import axios from "axios";
import { getRecentConsoleDiagnostics } from "./clientDiagnosticsBuffer";

const SENSITIVE_KEY = /password|token|secret|authorization|jwt|cookie|session|credential|api[_-]?key/i;

function redactString(value) {
  let s = String(value ?? "");
  if (s.length > 500 && /^eyJ/.test(s)) return "[jwt redacted]";
  if (/^Bearer\s+/i.test(s)) return "Bearer [redacted]";
  return s;
}

function redactValue(value, depth = 0) {
  if (value == null) return value;
  if (depth > 6) return "[nested]";
  if (typeof value === "string") return redactString(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) {
    return value.slice(0, 30).map((item) => redactValue(item, depth + 1));
  }
  if (typeof value === "object") {
    const out = {};
    for (const [key, val] of Object.entries(value)) {
      if (SENSITIVE_KEY.test(key)) {
        out[key] = "[redacted]";
      } else {
        out[key] = redactValue(val, depth + 1);
      }
    }
    return out;
  }
  return String(value);
}

export function sanitizeApiResponseData(data) {
  if (data == null) return "";
  try {
    if (typeof data === "string") return redactString(data);
    return JSON.stringify(redactValue(data), null, 0);
  } catch {
    return redactString(String(data));
  }
}

export function extractApiResponseSnippet(error, maxLen = 2000) {
  if (!error?.response?.data) return "";
  const raw = sanitizeApiResponseData(error.response.data);
  if (!raw) return "";
  if (raw.length <= maxLen) return raw;
  return `${raw.slice(0, maxLen - 18)}\n...(truncated)`;
}

export function extractStackTrace(error, maxLen = 3500) {
  if (!error) return "";
  const stack =
    (error instanceof Error && error.stack) ||
    (typeof error?.stack === "string" && error.stack) ||
    (error?.reason instanceof Error && error.reason.stack) ||
    "";
  const message =
    extractClientErrorMessage(error) ||
    (error instanceof Error ? error.message : "") ||
    "";
  let combined = stack || (message ? `Error: ${message}` : "");
  if (!combined) return "";
  combined = combined.replace(/\r\n/g, "\n");
  if (combined.length <= maxLen) return combined;
  return `${combined.slice(0, maxLen - 18)}\n...(truncated)`;
}

export function extractClientErrorMessage(err) {
  if (typeof err === "string") return err.trim();
  const data = err?.response?.data;
  if (typeof data?.error === "string") return data.error.trim();
  if (typeof data?.message === "string") return data.message.trim();
  if (typeof err?.message === "string") return err.message.trim();
  return "";
}

export function buildTechnicalDiagnostics(error, { includeConsole = true } = {}) {
  let clientStack = extractStackTrace(error);
  let apiResponseSnippet = "";

  if (axios.isAxiosError(error)) {
    apiResponseSnippet = extractApiResponseSnippet(error);
    if (!clientStack && error.message) {
      clientStack = extractStackTrace(new Error(error.message));
    }
  }

  const recentConsole = includeConsole ? getRecentConsoleDiagnostics() : "";

  return {
    clientStack: clientStack || "",
    apiResponseSnippet: apiResponseSnippet || "",
    recentConsole: recentConsole || "",
  };
}

/** Last error shown in support offer / API failure — used when user opens manual bug report. */
export function rememberReportableError(error) {
  if (typeof window === "undefined" || error == null) return;
  try {
    window.__NEWERA_LAST_REPORTABLE_ERROR__ = error;
  } catch {
    /* ignore */
  }
}

export function getLastReportableError() {
  if (typeof window === "undefined") return null;
  return window.__NEWERA_LAST_REPORTABLE_ERROR__ ?? null;
}
